import * as S from "./styles";
import React, { useMemo } from "react";
import { StPrice } from "@/components";
import { PackingTypes, formatCurrency } from "@/shared";

import { ItemsProps } from "@/talons/orderStatusPage/useOrderStatusPage";
import { PackagesTypesProps, PackagingProductProps } from "@/types";
import { useDiscountFlex } from "@/hooks";
import OrderStatusCounter from "./order-status-counter";
import { RiExchangeDollarLine } from "react-icons/ri";
import colors from "@/constants/systemColors";

interface CardProps {
  item: ItemsProps;
  isFirst?: boolean;
  addProductToCart?: any;
  updateProductAmount?: any;
  removeProductFromCart?: any;
}

// coerção segura p/ número
const n = (v: unknown, fallback = 0) => {
  const num = Number(v);
  return Number.isFinite(num) ? num : fallback;
};

const CartProductCard = ({ item, isFirst }: CardProps) => {
  const { getDiscountProduct } = useDiscountFlex();

  // Encontrar a embalagem selecionada (coerente com item.packaging "XX_QTD")
  const packing = useMemo(() => {
    return item?.EMBALAGEM?.find((i: PackagingProductProps) => {
      const [pack, qtd] = String(item?.packaging ?? "").split("_");
      const qty = Number(qtd);
      if (pack === "PK") {
        return i?.SG_EMBALAGEM === pack && i?.QTD_MULTIPLO_VENDA === qty;
      }
      return i?.SG_EMBALAGEM === pack && i?.QTD_EMBALAGEM === qty;
    });
  }, [item?.EMBALAGEM, item?.packaging]) as PackagingProductProps | undefined;

  const packaging = useMemo(() => {
    const [pack, qtd] = String(item?.packaging ?? "").split("_");
    return { pack, qtd };
  }, [item?.packaging]) as { pack: PackagesTypesProps; qtd: string };

  // --------- LÓGICA FLEX x NORMAL (usando nomes corretos) ---------
  const percentualFlex = n(item?.percentualFlex);       // string "0.94" -> 0.94
  const priceFlex = n(item?.price_flex);                // total (flex)
  const priceNormal = n(item?.price);                   // total (normal)

  const unitFlex = n(item?.unit_price_flex);            // unitário (flex)
  const unitNormal = n(item?.unitPrice);                // unitário (normal)

  const perItemFlex = n(item?.unit_price_per_item_flex);// /un. (flex)
  const perItemNormal = n(item?.unitPricePerItem);      // /un. (normal)

  const stFlex = n(item?.st_price_flex);                // ST (flex)
  const stNormal = n(item?.stPrice);                    // ST (normal)

  // condição para usar "flex"
  const showFlex = percentualFlex !== 0 && priceFlex > 0;

  const totalPrice = showFlex ? priceFlex : priceNormal;
  const unitPrice = showFlex ? unitFlex : unitNormal;
  const unitPricePerItem = showFlex ? perItemFlex : perItemNormal;
  const stPrice = showFlex ? stFlex : stNormal;
  // ---------------------------------------------------------------

  const amountProduct = useMemo(() => {
    const [pack, qtd] = String(item?.packaging ?? "").split("_");
    return getDiscountProduct({
      package: pack,
      quantity: Number(qtd),
      productId: item?.COD_PRODUTO,
    });
  }, [item, getDiscountProduct]);

  if (item?.quantity === 0) return null;

  // EAN pode vir em diferentes chaves dentro de ATRIBUTOS
  const ean =
    item?.ATRIBUTOS?.find((at: any) => at?.ean_grp)?.ean_grp ??
    item?.ATRIBUTOS?.find((at: any) => at?.EAN) ??
    "-";

  return (
    <S.Container $isfirst={isFirst}>
      <S.Subcontainer>
        <S.ProductImage
          alt={item?.product}
          src={item?.IMAGEM}
        />

        <S.ProductDescriptionRow>
          <S.ProductDescription>{item?.product}</S.ProductDescription>

          {percentualFlex !== 0 && (
            <S.FlexPercentage $positive={percentualFlex > 0}>
              <RiExchangeDollarLine size={20} color={colors.brand.grafite} />
              <S.FlexPercentageLabel>
                {percentualFlex.toFixed(2)}%
              </S.FlexPercentageLabel>
            </S.FlexPercentage>
          )}
        </S.ProductDescriptionRow>

        {totalPrice > 0 ? (
          <S.PriceBlock>
            {/* Total */}
            <S.PriceRow>{formatCurrency({ value: totalPrice })}</S.PriceRow>

            {/* Unitário (cx/pct etc.) */}
            <S.Price>{formatCurrency({ value: unitPrice })}</S.Price>

            {/* ST */}
            {stPrice > 0 && <StPrice precoSt={stPrice} />}

            {/* Por unidade (/un.) */}
            <S.UnityPrice>
              {formatCurrency({ value: unitPricePerItem })}/un.
            </S.UnityPrice>
          </S.PriceBlock>
        ) : null}
      </S.Subcontainer>

      <S.Subcontainer $alignitems="flex-end">
        <S.ButtonContainer>
          <S.RemoveButton onClick={() => {}}>
            <S.RemoveButtonLabel>{/* Remover */}</S.RemoveButtonLabel>
          </S.RemoveButton>
        </S.ButtonContainer>

        <S.DetailBlock>
          <S.ProductCodes>
            <S.ProductCode>Cód: {item?.sku}</S.ProductCode>
            <S.ProductCode>EAN: {String(ean)}</S.ProductCode>
          </S.ProductCodes>

          <S.PackingBlock>
            <S.IconContainer $radius={4}>
              <S.Packing src={PackingTypes?.[packaging?.pack]?.icon} />
            </S.IconContainer>
            <S.PackingDescription>
              <S.PackingLabel>
                {PackingTypes?.[packaging?.pack]?.packingTitle}
              </S.PackingLabel>
              <S.PackingUnities>{packaging?.qtd} unidades</S.PackingUnities>
            </S.PackingDescription>
          </S.PackingBlock>
        </S.DetailBlock>

        <S.ButtonContainer>
          {totalPrice <= 0 ? (
            <S.Unavailable>Indisponível</S.Unavailable>
          ) : (
            <S.CounterWrapper>
              <OrderStatusCounter
                id={item?.sku}
                quantity={item?.quantity}
                background="light"
                showBorder={true}
              />
            </S.CounterWrapper>
          )}
        </S.ButtonContainer>
      </S.Subcontainer>
    </S.Container>
  );
};

export default CartProductCard;
